/**
 * Pure helpers for importing hydrants from the global POI sources
 * (GlobalPOIFiles metadata in Firestore + tiled/chunked/single JSON files in
 * Firebase Storage, uploaded via the staff Global POI page).
 *
 * IMPORTANT: hydrant identity here mirrors the mobile apps exactly
 * (LadderApp screens/Hydrants/HydrantList.js isHydrantType/getHydrantId and
 * HydrantDetail.js sanitizeDocId), so imported records line up with the
 * records the apps create/merge for the same POIs.
 *
 * No Firebase imports — keep this module pure so it stays unit-testable.
 */

/** Slippy-map tile math (mirror of GlobalHydrantsPOI.jsx deg2num). */
export function deg2num(latDeg, lonDeg, zoom) {
  const latRad = (latDeg * Math.PI) / 180.0;
  const n = Math.pow(2.0, zoom);
  const x = Math.floor(((lonDeg + 180.0) / 360.0) * n);
  const y = Math.floor(((1.0 - Math.asinh(Math.tan(latRad)) / Math.PI) / 2.0) * n);
  return { x, y };
}

/** All tile keys ("z/x/y") covering a bounds at one zoom (capped for safety). */
export function tileKeysForBounds(bounds, zoom, maxTiles = 4096) {
  const { north, south, east, west } = bounds;
  const a = deg2num(north, west, zoom);
  const b = deg2num(south, east, zoom);
  const xMin = Math.min(a.x, b.x);
  const xMax = Math.max(a.x, b.x);
  const yMin = Math.min(a.y, b.y);
  const yMax = Math.max(a.y, b.y);
  const keys = [];
  for (let x = xMin; x <= xMax; x += 1) {
    for (let y = yMin; y <= yMax; y += 1) {
      keys.push(`${zoom}/${x}/${y}`);
      if (keys.length > maxTiles) return keys; // caller decides how to handle
    }
  }
  return keys;
}

/**
 * Bounds we cannot trust for exclusion decisions: missing, non-numeric, or the
 * all-zero box the Global POI uploader produces for payloads whose coordinates
 * it can't read (GeoJSON features, WKT, Shape_X/Y — it only looks at
 * latitude/lat + longitude/lng/lon, defaulting to 0).
 */
export function isUnreliableBounds(b) {
  if (!b) return true;
  const n = (v) => (typeof v === "string" ? parseFloat(v) : v);
  const vals = [b.north, b.south, b.east, b.west].map(n);
  if (!vals.every(Number.isFinite)) return true;
  return vals.every((v) => v === 0);
}

/** Do two {north,south,east,west} boxes overlap? Unreliable bounds => assume yes. */
export function boundsIntersect(a, b) {
  if (isUnreliableBounds(a) || isUnreliableBounds(b)) return true;
  const n = (v) => (typeof v === "string" ? parseFloat(v) : v);
  return !(n(a.south) > n(b.north) || n(a.north) < n(b.south) || n(a.west) > n(b.east) || n(a.east) < n(b.west));
}

/** Parse "POINT (lng lat)" WKT (the agency POI transform emits this shape). */
export function parseWktPoint(wkt) {
  if (typeof wkt !== "string") return null;
  const m = wkt.match(/POINT\s*\(\s*(-?\d+(?:\.\d+)?)\s+(-?\d+(?:\.\d+)?)\s*\)/i);
  if (!m) return null;
  return { longitude: parseFloat(m[1]), latitude: parseFloat(m[2]) };
}

/**
 * Flatten a raw POI record (plain object or GeoJSON feature) into one object
 * with guaranteed numeric latitude/longitude (or null when unlocatable).
 */
export function normalizePoi(raw) {
  if (!raw || typeof raw !== "object") return null;
  // GeoJSON feature: merge properties up. Properties must win over the
  // envelope — otherwise the feature's own `type: "Feature"` clobbers a
  // `properties.type: "hydrant"` and the POI fails hydrant detection.
  const flat = raw.properties && typeof raw.properties === "object" ? { ...raw, ...raw.properties } : { ...raw };

  let lat =
    flat.latitude ?? flat.lat ?? flat.Shape_Y ?? flat.SHAPE_Y ?? flat.y ?? null;
  let lng =
    flat.longitude ?? flat.lng ?? flat.lon ?? flat.Shape_X ?? flat.SHAPE_X ?? flat.x ?? null;

  if ((lat == null || lng == null) && raw.geometry && Array.isArray(raw.geometry.coordinates)) {
    const [gLng, gLat] = raw.geometry.coordinates;
    lng = lng ?? gLng;
    lat = lat ?? gLat;
  }
  if ((lat == null || lng == null) && flat.WKT) {
    const pt = parseWktPoint(flat.WKT);
    if (pt) {
      lat = lat ?? pt.latitude;
      lng = lng ?? pt.longitude;
    }
  }

  lat = typeof lat === "string" ? parseFloat(lat) : lat;
  lng = typeof lng === "string" ? parseFloat(lng) : lng;
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
  return { ...flat, latitude: lat, longitude: lng };
}

/** Mirror of the mobile apps' isHydrantType (HydrantList.js). */
export function isHydrantPoi(poi) {
  if (!poi) return false;
  const type = String(poi.type || poi.Type || poi.category || poi.POI_TYPE || "").toLowerCase();
  if (type.includes("hydrant")) return true;
  if (poi.HYDRANT === 1 || poi.HYDRANT === "1") return true;
  if (poi.HYDRANT_ID || poi.HYDRANT_NU) return true;
  const title = String(poi.title || poi.POI_NAME || "").toLowerCase();
  const desc = String(poi.description || poi.POI_DESCRIPTION || "").toLowerCase();
  if (title.includes("hydrant") || desc.includes("hydrant")) return true;
  return false;
}

/** Mirror of the mobile apps' getHydrantId (HydrantList.js). */
export function getHydrantIdFromPoi(poi) {
  return String(poi.HYDRANT_ID || poi.HYDRANT_NU || poi.title || poi.POI_NAME || "").trim();
}

/** Mirror of the mobile apps' sanitizeDocId (HydrantDetail.js). */
export function sanitizeDocId(id) {
  return id ? String(id).replace(/[/.]/g, "_").trim() : "";
}

// ID values that aren't real per-hydrant identifiers — some source datasets
// stuff "N/A" (etc.) into every hydrant's ID field, which would otherwise
// collapse thousands of distinct hydrants onto one Firestore doc.
const PLACEHOLDER_IDS = new Set(["", "n/a", "n_a", "na", "none", "null", "nil", "unknown", "-", "0"]);

// Collapse anything that isn't a safe Firestore doc-id character to "_" and
// trim leading/trailing underscores. Source IDs like "0          {GUID}" carry
// spaces/braces; keeping the id strictly [A-Za-z0-9_-] avoids any path/rule
// edge cases and keeps the ids readable.
const safeDocKey = (s) => String(s || "").replace(/[^A-Za-z0-9_-]+/g, "_").replace(/^_+|_+$/g, "");

/**
 * Stable, unique Firestore doc key for a global-POI hydrant. Prefers the real
 * HYDRANT_ID/HYDRANT_NU/title; when that's absent or a placeholder ("N/A"),
 * falls back to the hydrant's coordinates — unique per physical hydrant and
 * stable across re-imports, so distinct hydrants never merge and the same
 * hydrant never duplicates. Returns "" only when there's no id AND no location.
 */
export function deriveHydrantDocKey(poi) {
  if (!poi) return "";
  const raw = safeDocKey(getHydrantIdFromPoi(poi));
  if (raw && !PLACEHOLDER_IDS.has(raw.toLowerCase())) return raw;
  const lat = Number(poi.latitude);
  const lng = Number(poi.longitude);
  if (Number.isFinite(lat) && Number.isFinite(lng)) {
    return safeDocKey(`hyd_${lat.toFixed(6)}_${lng.toFixed(6)}`);
  }
  return "";
}

const GUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** Display alias: skip GUIDs and bare numbers (mirrors the mobile clean-title rule). */
export function cleanAlias(title) {
  const t = String(title || "").trim();
  if (!t || GUID_RE.test(t) || /^\d+$/.test(t)) return "";
  return t;
}

export function isInsideBounds(poi, bounds) {
  const n = (v) => (typeof v === "string" ? parseFloat(v) : v);
  return (
    poi.latitude <= n(bounds.north) &&
    poi.latitude >= n(bounds.south) &&
    poi.longitude <= n(bounds.east) &&
    poi.longitude >= n(bounds.west)
  );
}

/**
 * Ray-casting point-in-polygon. `ring` is [{lat,lng},...] (closed or open —
 * the same shape the zone-alerts / response-area editors store).
 */
export function pointInPolygon(point, ring) {
  if (!Array.isArray(ring) || ring.length < 3) return false;
  const x = point.longitude ?? point.lng;
  const y = point.latitude ?? point.lat;
  let inside = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const xi = ring[i].lng;
    const yi = ring[i].lat;
    const xj = ring[j].lng;
    const yj = ring[j].lat;
    const intersects = yi > y !== yj > y && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi;
    if (intersects) inside = !inside;
  }
  return inside;
}

/** Envelope {north,south,east,west} of [{points:[{lat,lng}]}] polygons. */
export function polygonsBounds(polygons) {
  let north = -Infinity;
  let south = Infinity;
  let east = -Infinity;
  let west = Infinity;
  (polygons || []).forEach((poly) => {
    (poly.points || poly.polygon || []).forEach((p) => {
      if (!Number.isFinite(p?.lat) || !Number.isFinite(p?.lng)) return;
      if (p.lat > north) north = p.lat;
      if (p.lat < south) south = p.lat;
      if (p.lng > east) east = p.lng;
      if (p.lng < west) west = p.lng;
    });
  });
  if (!Number.isFinite(north)) return null;
  return { north, south, east, west };
}

/**
 * Is the POI inside the import area? area = { polygons?, bounds? }.
 * With polygons: bbox pre-check then point-in-polygon against any polygon.
 * Without polygons: plain bounds check.
 */
export function isInsideArea(poi, area) {
  const bounds = area.bounds || polygonsBounds(area.polygons);
  if (bounds && !isInsideBounds(poi, bounds)) return false;
  const polys = (area.polygons || []).filter((p) => (p.points || p.polygon || []).length >= 3);
  if (!polys.length) return !!bounds;
  return polys.some((p) => pointInPolygon(poi, p.points || p.polygon));
}

/**
 * Map a normalized global POI to a new Agencies/{agency}/Hydrants document.
 * Pure: caller adds serverTimestamp audit fields.
 */
export function mapPoiToHydrantDoc(poi, source = {}) {
  // The stored hydrantId must match the doc key so re-imports dedupe correctly
  // and datasets with placeholder IDs ("N/A") don't all share one hydrantId.
  const hydrantId = deriveHydrantDocKey(poi);
  const title = String(poi.title || poi.POI_NAME || poi.HYDRANT_NU || poi.HYDRANT_ID || "").trim();
  return {
    hydrantId,
    alias: cleanAlias(title),
    poiTitle: title || hydrantId,
    streetAddress: String(poi.POI_ADDRESS || poi.address || poi.ADDRESS || poi.FULLNAME || "").trim(),
    town: String(poi.TOWN || poi.town || poi.CITY || poi.city || "").trim(),
    crossStreets: "",
    latitude: poi.latitude,
    longitude: poi.longitude,
    hydrantType: "unknown",
    ownership: "unknown",
    condition: "good",
    colorCode: "unknown",
    inService: true,
    notes: String(poi.description || poi.POI_DESCRIPTION || "").trim(),
    importedFrom: "global-poi",
    importSource: {
      fileId: source.fileId || "",
      fileName: source.fileName || "",
      version: source.version ?? null,
    },
  };
}

/**
 * Extract the POI array from any of the stored JSON payload shapes:
 * tile ({pois}), chunk ({data}), single ({data}), raw array, GeoJSON ({features}).
 */
export function extractPoiArray(json) {
  if (!json) return [];
  if (Array.isArray(json)) return json;
  if (Array.isArray(json.pois)) return json.pois;
  if (Array.isArray(json.data)) return json.data;
  if (Array.isArray(json.features)) return json.features;
  return [];
}
